/**
 * /compliance/fica — FICA compliance posture.
 *
 * Public reference page for prospective firms evaluating Elreign. Cites the
 * real FIC Act sections that the platform supports and the boundary between
 * Elreign's posture and the firm's own MLRO duties.
 *
 * Replaces the Phase 9 ComingSoonShell stub per Phase 3 Task 3.7 of the
 * 2026-05-16 implementation plan.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/compliance/fica')({
  component: ComplianceFicaPage,
});

const TOC = [
  { anchor: 'overview',     label: 'Where Elreign fits' },
  { anchor: 's23',          label: 's23 · Record-keeping' },
  { anchor: 's24a',         label: 's24A · Beneficial ownership' },
  { anchor: 's43b',         label: 's43B · MLRO designation' },
  { anchor: 'screening',    label: 'Sanctions and PEP screening' },
  { anchor: 'reporting',    label: 'STR and CTR via goAML' },
  { anchor: 'rmcp',         label: 'Risk Management and Compliance Programme' },
  { anchor: 'firm-side',    label: 'What remains the firm’s responsibility' },
];

function ComplianceFicaPage() {
  useEffect(() => {
    document.title = 'FICA compliance · Elreign: what we do, what your firm does';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'How Elreign supports a fiduciary firm’s FIC Act obligations: s23 record-keeping with WORM audit chain, s24A beneficial ownership register, s43B MLRO designation, and goAML eFiling integration.',
    );
  }, []);

  return (
    <LegalLayout heading="FICA compliance." lastUpdated="17 May 2026" toc={TOC}>
      <p>
        Elreign supports the operational obligations under the Financial Intelligence Centre Act
        (Act 38 of 2001, as amended) that arise from administering trusts. This page describes
        what the platform does, what controls are in place, and what remains your firm&apos;s
        own responsibility as an accountable institution.
      </p>

      <h2 id="overview">Where Elreign fits</h2>
      <p>
        Fiduciary firms administering trusts are accountable institutions under Schedule 1 of the
        FIC Act (Items 2 and 12). The firm is responsible for its Risk Management and Compliance
        Programme, the designation of a Money Laundering Reporting Officer (MLRO), the
        submission of regulatory reports, and the keeping of records to the standard required by
        s23. Elreign provides the platform on which these obligations are operationalised.
      </p>

      <h2 id="s23">s23 · Record-keeping</h2>
      <p>
        FIC Act s23 requires accountable institutions to keep records of customer identification,
        the nature of the business relationship, and every transaction in respect of which a
        record is kept under s22, for a minimum of five years from the end of the business
        relationship.
      </p>
      <p>
        Elreign holds these records in a WORM-locked audit chain on Azure Blob Storage with
        immutability enforced at container level. Records carry a SHA-256 content hash and a
        chain hash linking each record to its predecessor. Records cannot be modified or deleted,
        even by Elreign staff. The default retention is five years from the date of the last
        chain event; longer windows are configured per tenant on request.
      </p>

      <h2 id="s24a">s24A · Beneficial ownership</h2>
      <p>
        FIC Act s24A requires accountable institutions to establish and maintain a record of the
        beneficial owners of a legal person, trust, or partnership in respect of which the
        institution provides services. Elreign maintains the beneficial owner register for each
        trust onboarded, automatically updates the register as trustees, beneficiaries, or
        founders change, and keeps the most current version ready for inspection at any
        time.
      </p>
      <p>
        The register format conforms to the Master of the High Court&apos;s ICMS schema. Bulk
        submission to the Master&apos;s registry is available; per-trust submission is the
        default at onboarding.
      </p>

      <h2 id="s43b">s43B · MLRO designation</h2>
      <p>
        FIC Act s43B requires the appointment of a Money Laundering Reporting Officer with
        oversight authority across the firm&apos;s anti-money-laundering programme. Elreign
        provides an in-product designation flow that records the MLRO appointment with effective
        date, scope, and successor planning. Re-designation is supported with full audit trail.
      </p>

      <h2 id="screening">Sanctions and PEP screening</h2>
      <p>
        Sanctions and politically-exposed-person screening is performed by your firm as the
        accountable institution, in line with its Risk Management and Compliance Programme.
        Elreign records the screening outcome per beneficial owner (list checked, date, result
        and reviewer) and seals it on the audit chain with timestamp, giving the firm&apos;s
        FICA review cycle a tamper-evident screening history. Direct integration with screening
        list providers is on the product roadmap and is not yet live.
      </p>

      <h2 id="reporting">STR and CTR via goAML</h2>
      <p>
        Suspicious transaction reports (STR) and cash threshold reports (CTR) are produced in
        goAML XML format. The MLRO designated under s43B reviews and authorises each submission
        under a four-eye rule, and the submission record is sealed on the audit chain. Direct
        eFiling submission to the Financial Intelligence Centre activates once the firm&apos;s
        goAML registration and credentials are configured; until then the authorised report is
        exported for filing through the firm&apos;s existing goAML channel.
      </p>

      <h2 id="rmcp">Risk Management and Compliance Programme</h2>
      <p>
        The platform supports your RMCP: risk rating per trust against your firm&apos;s policy
        thresholds, periodic review cadence (annual, two-yearly, three-yearly based on rating),
        and an annual compliance calendar with prompts ahead of statutory deadlines. The RMCP
        itself remains a firm-owned document; the platform records its application.
      </p>

      <h2 id="firm-side">What remains the firm&apos;s responsibility</h2>
      <p>
        The platform does not replace your firm&apos;s judgement, policy, or accountability. You
        remain responsible for:
      </p>
      <ul>
        <li>The design, approval, and review of your Risk Management and Compliance Programme.</li>
        <li>The decision to onboard, decline, exit, or escalate any particular client relationship.</li>
        <li>The MLRO&apos;s judgement on whether a transaction meets the threshold for a suspicious or unusual report.</li>
        <li>Filings with regulators and the Master of the High Court that require firm-level authorisation.</li>
        <li>Training of staff on AML/CFT obligations and platform operating procedures.</li>
      </ul>

      <p style={{ marginTop: 32 }}>
        See also: <Link to="/compliance/popia">POPIA compliance posture</Link>,{' '}
        <Link to="/legal/dpa">Data Processing Agreement</Link>,{' '}
        <Link to="/legal/security">Security disclosure</Link>.
      </p>
    </LegalLayout>
  );
}
